// assets/variant-resolver.js

(() => {
  console.debug('[resolver] file loaded');

  const GRAPHQL_ENDPOINT = '/api/2024-01/graphql.json';

  const query = `
    query getAllVariants($id: ID!, $cursor: String) {
      product: node(id: $id) {
        ... on Product {
          id
          title
          options {
            name
            values
          }
          variants(first: 250, after: $cursor) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                title
                availableForSale
                quantityAvailable
                image {
                  id
                  url
                  altText
                }
                price: priceV2 {
                  amount
                  currencyCode
                }
                compareAtPrice: compareAtPriceV2 {
                  amount
                  currencyCode
                }
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
        }
      }
    }
  `;

  function extractNumericId(gid) {
    if (!gid) return '';
    const parts = gid.split('/');
    return parts[parts.length - 1] || '';
  }

  async function fetchAllVariants(productGID, storefrontToken) {
    console.debug('[resolver] fetching variants via Storefront API');

    let cursor = null;
    let allEdges = [];
    let productMeta = null;
    let page = 1;

    while (true) {
      console.debug('[resolver] requesting page', page, 'cursor:', cursor);

      const res = await fetch(GRAPHQL_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontToken
        },
        body: JSON.stringify({
          query,
          variables: { id: productGID, cursor }
        })
      });

      console.debug('[resolver] response status:', res.status);

      if (!res.ok) {
        console.error('[resolver] API error', res.status, res.statusText);
        throw new Error('Storefront API error');
      }

      const json = await res.json();
      const product = json?.data?.product;

      if (!product) {
        console.error('[resolver] no product in GraphQL response', json);
        throw new Error('No product data');
      }

      if (!productMeta) {
        productMeta = {
          id: product.id,
          title: product.title,
          options: product.options
        };
      }

      const { pageInfo, edges } = product.variants;
      console.debug(
        '[resolver] page',
        page,
        '- edges:',
        edges.length,
        'hasNextPage:',
        pageInfo?.hasNextPage
      );

      allEdges = allEdges.concat(edges);

      if (!pageInfo?.hasNextPage) break;
      cursor = pageInfo.endCursor;
      page += 1;
    }

    const variants = allEdges.map((edge) => {
      const node = edge.node;
      const mediaId = node.image?.id ? extractNumericId(node.image.id) : null;

      return {
        gid: node.id,
        id: extractNumericId(node.id),
        title: node.title,
        available: node.availableForSale,
        qty: node.quantityAvailable,
        image: node.image,
        mediaId, // numeric media id (matches data-media-id)
        price: node.price,
        compareAtPrice: node.compareAtPrice,
        selectedOptions: node.selectedOptions
      };
    });

    console.debug('[resolver] fetched variants count:', variants.length);

    return {
      ...productMeta,
      variants
    };
  }

  // ---------- UI helpers ----------

  function createOptionUI(root, product, variants) {
    const { options } = product;
    root.innerHTML = '';

    const wrapper = document.createElement('div');
    wrapper.className = 'super-variant-wrapper';

    (options || []).forEach((opt, idx) => {
      const group = document.createElement('div');
      group.className = 'super-option-group';
      group.dataset.optionIndex = idx.toString();
      group.dataset.optionName = opt.name;

      const label = document.createElement('div');
      label.className = 'super-option-label';
      label.textContent = opt.name;
      group.appendChild(label);

      const list = document.createElement('div');
      list.className = 'super-option-values';

      const optionNameLower = (opt.name || '').toLowerCase();
      const isColorOption =
        optionNameLower.includes('color') || optionNameLower.includes('colour');

      (opt.values || []).forEach((value) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.dataset.optionIndex = idx.toString();
        btn.dataset.optionName = opt.name;
        btn.dataset.optionValue = value;
        btn.setAttribute('aria-pressed', 'false');

        if (isColorOption) {
          btn.className = 'super-option-value super-option-value--swatch';

          const swatch = document.createElement('div');
          swatch.className = 'super-swatch';

          const vForColor = variants.find(
            (v) =>
              v.selectedOptions.some(
                (so) => so.name === opt.name && so.value === value
              ) && v.image
          );

          if (vForColor && vForColor.image && vForColor.image.url) {
            swatch.style.backgroundImage = `url(${vForColor.image.url})`;
          } else {
            swatch.style.backgroundColor = value.toLowerCase();
          }

          const text = document.createElement('span');
          text.className = 'super-option-value-label';
          text.textContent = value;

          btn.appendChild(swatch);
          btn.appendChild(text);
        } else {
          btn.className = 'super-option-value';
          btn.textContent = value;
        }

        list.appendChild(btn);
      });

      group.appendChild(list);
      wrapper.appendChild(group);
    });

    root.appendChild(wrapper);
  }

  function buildSelectedOptions(product, variants, root) {
    const selected = {};

    const formId = root.dataset.productFormId;
    let form = null;
    if (formId) form = document.getElementById(formId);
    if (!form) form = root.closest('form');

    let currentVariant = null;
    if (form) {
      const idInput = form.querySelector('input[name="id"]');
      const currentId = idInput?.value;
      if (currentId) {
        currentVariant = variants.find((v) => v.id === currentId);
      }
    }

    if (currentVariant) {
      (currentVariant.selectedOptions || []).forEach((so) => {
        selected[so.name] = so.value;
      });
    } else {
      (product.options || []).forEach((opt) => {
        if (opt.values && opt.values.length > 0) {
          selected[opt.name] = opt.values[0];
        }
      });
    }

    return selected;
  }

  function findMatchingVariant(variants, selectedOptions) {
    return variants.find((variant) =>
      variant.selectedOptions.every(
        (so) => selectedOptions[so.name] === so.value
      )
    );
  }

  function updateButtonState(buttons, selectedOptions) {
    buttons.forEach((btn) => {
      const name = btn.dataset.optionName;
      const value = btn.dataset.optionValue;
      const isActive = selectedOptions[name] === value;
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      btn.classList.toggle('is-active', isActive);
    });
  }

  // ---- Gallery sync: use data-media-id first, then filename as a fallback ----

  function syncGalleryToVariant(variant) {
    if (!variant) return;

    const mediaRoot = document.getElementById('product-media');
    if (!mediaRoot) {
      console.debug('[resolver] no #product-media found');
      return;
    }

    // 1) Best path: click thumbnail by mediaId
    if (variant.mediaId) {
      const thumbBtn = mediaRoot.querySelector(
        `.media-thumbs__item[data-media-id="${variant.mediaId}"] button`
      );
      if (thumbBtn) {
        console.debug('[resolver] clicking thumb via mediaId', variant.mediaId);
        thumbBtn.dispatchEvent(
          new MouseEvent('click', { bubbles: true, cancelable: true })
        );
        return;
      }
    }

    // 2) Fallback: match by file name among thumbs only
    if (variant.image && variant.image.url) {
      const targetUrl = variant.image.url.split('?')[0];
      const targetFile = targetUrl.split('/').pop();

      const imgs = mediaRoot.querySelectorAll('.media-thumbs__item img');
      let matchingBtn = null;

      imgs.forEach((img) => {
        const src = (img.currentSrc || img.src || '').split('?')[0];
        if (!src) return;
        const file = src.split('/').pop();
        if (file === targetFile) {
          const btn = img.closest('button');
          if (btn) matchingBtn = btn;
        }
      });

      if (matchingBtn) {
        console.debug('[resolver] clicking thumb via filename', targetFile);
        matchingBtn.dispatchEvent(
          new MouseEvent('click', { bubbles: true, cancelable: true })
        );
        return;
      }
    }

    console.debug('[resolver] no matching thumb found for variant');
  }

  function updateFormWithVariant(root, variant) {
    const formId = root.dataset.productFormId;
    let form = null;

    if (formId) {
      form = document.getElementById(formId);
    }
    if (!form) {
      form = root.closest('form');
    }

    if (!form) {
      console.warn('[resolver] no product form found');
      return;
    }

    const idInput = form.querySelector('input[name="id"]');
    if (!idInput) {
      console.warn('[resolver] no input[name="id"] in form');
      return;
    }

    const submitBtn = form.querySelector('[type="submit"]');

    if (!variant || !variant.available) {
      idInput.value = '';
      if (submitBtn) submitBtn.setAttribute('disabled', 'disabled');
    } else {
      idInput.value = variant.id;
      if (submitBtn) submitBtn.removeAttribute('disabled');
    }

    updatePriceDisplay(variant);
    updateImage(variant);
    syncGalleryToVariant(variant);
  }

  function updatePriceDisplay(variant) {
    if (!variant || !variant.price) return;

    const priceEl = document.querySelector('[data-product-price]');
    const compareEl = document.querySelector('[data-compare-price]');

    if (priceEl) {
      priceEl.textContent = formatMoney(
        variant.price.amount,
        variant.price.currencyCode
      );
    }

    if (compareEl) {
      if (
        variant.compareAtPrice &&
        parseFloat(variant.compareAtPrice.amount) >
          parseFloat(variant.price.amount)
      ) {
        compareEl.textContent = formatMoney(
          variant.compareAtPrice.amount,
          variant.compareAtPrice.currencyCode
        );
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }
  }

  function formatMoney(amount, currencyCode) {
    const value = Number(amount || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode || 'GBP'
      }).format(value);
    } catch (e) {
      return value.toFixed(2);
    }
  }

  function updateImage(variant) {
    if (!variant || !variant.image || !variant.image.url) return;

    let mainImg =
      document.querySelector('[data-product-main-image]') ||
      document.querySelector('#product-media img') ||
      document.querySelector('.product-media img');

    if (!mainImg) return;

    mainImg.src = variant.image.url;
    if (variant.image.altText) {
      mainImg.alt = variant.image.altText;
    }
  }

  // ---------- Initialise ----------

  document.addEventListener('DOMContentLoaded', async () => {
    const root = document.getElementById('super-variant-root');
    if (!root) {
      console.debug('[resolver] no super-variant-root, skipping');
      return;
    }

    console.debug('[resolver] root found:', root);

    const productGID = root.dataset.productGid;
    const storefrontToken = root.dataset.storefrontToken;

    console.debug('[resolver] data-product-gid:', productGID);
    console.debug('[resolver] data-storefront-token:', storefrontToken);

    if (!productGID || !storefrontToken) {
      console.warn(
        '[resolver] missing productGID or storefrontToken – aborting'
      );
      return;
    }

    try {
      const product = await fetchAllVariants(productGID, storefrontToken);
      const variants = product.variants || [];

      if (!variants.length) {
        console.warn('[resolver] no variants received from API');
        return;
      }

      console.debug(
        '[resolver] ready with',
        variants.length,
        'variants from Storefront API'
      );

      createOptionUI(root, product, variants);

      const buttons = root.querySelectorAll('.super-option-value');
      const selectedOptions = buildSelectedOptions(product, variants, root);

      updateButtonState(buttons, selectedOptions);
      const initialVariant = findMatchingVariant(variants, selectedOptions);
      if (!initialVariant) {
        console.warn('[resolver] no matching variant for default selection');
      }
      updateFormWithVariant(root, initialVariant);

      buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const name = btn.dataset.optionName;
          const value = btn.dataset.optionValue;

          selectedOptions[name] = value;
          updateButtonState(buttons, selectedOptions);

          const variant = findMatchingVariant(variants, selectedOptions);
          if (!variant) {
            console.warn(
              '[resolver] no variant found for selection',
              selectedOptions
            );
          }
          updateFormWithVariant(root, variant);
        });
      });
    } catch (err) {
      console.error('[resolver] fatal error', err);
    }
  });
})();

